import { createDoubleTapTracker } from './double-tap';

/**
 * Zoom + pan for an SVG stage by rewriting its `viewBox`.
 *
 * Gestures (listeners on the `<svg>`): wheel zooms about the cursor; a single
 * pointer drag pans; two pointers pinch (scale about the midpoint + pan with
 * it); double-click, or a double-tap on touch, resets. Elements carrying
 * `data-no-pan` (drag handles) never start a pan. The helper reports the
 * visible domain so experiments can re-tick axes and re-sample curves, and —
 * when `fontSize` is given — keeps text screen-stable by setting the svg
 * `font-size` attribute to `fontSize / zoom`.
 */

export interface ViewBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface ViewportView {
  /** Current SVG viewBox. */
  viewBox: ViewBox;
  /** base.width / viewBox.width (1 = base view). */
  zoom: number;
  /** Visible x range. */
  xDomain: [number, number];
  /** Visible y range; math-up when `yUp` (the stage has a scale(1 -1) world group). */
  yDomain: [number, number];
}

export interface ZoomViewportOptions {
  base: ViewBox;
  /** Default 0.25. */
  minZoom?: number;
  /** Default 8. */
  maxZoom?: number;
  /** true → yDomain = [-(y + h), -y]. Default false. */
  yUp?: boolean;
  /** Base svg font-size; when given, font-size = fontSize / zoom on every change. */
  fontSize?: number;
  onChange(view: ViewportView): void;
  /** Default: `!target.closest('[data-no-pan]')`. */
  isPanTarget?(target: Element): boolean;
}

export interface ZoomViewportHandle {
  getView(): ViewportView;
  /** Clamp zoom to the limits (keeping the centre), apply, notify. */
  setView(viewBox: ViewBox): void;
  reset(): void;
  dispose(): void;
}

const WHEEL_SENSITIVITY = 0.0015;
const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));
const fmt = (n: number): string => String(Number(n.toFixed(5)));

/** px per viewBox unit for preserveAspectRatio="xMidYMid meet". */
export function unitScale(rect: DOMRectReadOnly, viewBox: ViewBox): number {
  return Math.min(rect.width / viewBox.width, rect.height / viewBox.height);
}

/** Client pixel → viewBox units, honouring the meet letterbox. */
export function clientToViewBox(
  rect: DOMRectReadOnly,
  viewBox: ViewBox,
  clientX: number,
  clientY: number,
): { x: number; y: number } {
  const scale = unitScale(rect, viewBox);
  const offsetX = (rect.width - viewBox.width * scale) / 2;
  const offsetY = (rect.height - viewBox.height * scale) / 2;
  return {
    x: viewBox.x + (clientX - rect.left - offsetX) / scale,
    y: viewBox.y + (clientY - rect.top - offsetY) / scale,
  };
}

/** Scale `viewBox` by `factor` about `anchor` (viewBox units), clamping zoom relative to `base`. */
export function zoomViewBoxAbout(
  viewBox: ViewBox,
  anchor: { x: number; y: number },
  factor: number,
  base: ViewBox,
  limits: { minZoom: number; maxZoom: number },
): ViewBox {
  const zoomNow = base.width / viewBox.width;
  const zoomNext = clamp(zoomNow * factor, limits.minZoom, limits.maxZoom);
  const f = zoomNow / zoomNext; // width multiplier
  return {
    x: anchor.x - (anchor.x - viewBox.x) * f,
    y: anchor.y - (anchor.y - viewBox.y) * f,
    width: viewBox.width * f,
    height: viewBox.height * f,
  };
}

export function createZoomViewport(
  svg: SVGSVGElement,
  opts: ZoomViewportOptions,
): ZoomViewportHandle {
  const minZoom = opts.minZoom ?? 0.25;
  const maxZoom = opts.maxZoom ?? 8;
  const limits = { minZoom, maxZoom };
  const base: ViewBox = { ...opts.base };
  function isPanTarget(target: Element): boolean {
    return opts.isPanTarget ? opts.isPanTarget(target) : target.closest('[data-no-pan]') === null;
  }

  let vb: ViewBox = { ...base };
  const pointers = new Map<number, { x: number; y: number }>();
  const doubleTap = createDoubleTapTracker();
  let disposed = false;

  const prevTouchAction = svg.style.touchAction;
  svg.style.touchAction = 'none';

  function currentView(): ViewportView {
    const zoom = base.width / vb.width;
    const yDomain: [number, number] = opts.yUp
      ? [-(vb.y + vb.height), -vb.y]
      : [vb.y, vb.y + vb.height];
    return { viewBox: { ...vb }, zoom, xDomain: [vb.x, vb.x + vb.width], yDomain };
  }

  function apply(next: ViewBox): void {
    vb = next;
    svg.setAttribute('viewBox', `${fmt(vb.x)} ${fmt(vb.y)} ${fmt(vb.width)} ${fmt(vb.height)}`);
    if (opts.fontSize !== undefined) {
      svg.setAttribute('font-size', fmt(opts.fontSize / (base.width / vb.width)));
    }
    opts.onChange(currentView());
  }

  function setView(next: ViewBox): void {
    doubleTap.cancel();
    const zoom = clamp(base.width / next.width, minZoom, maxZoom);
    const width = base.width / zoom;
    const height = width * (base.height / base.width);
    const cx = next.x + next.width / 2;
    const cy = next.y + next.height / 2;
    apply({ x: cx - width / 2, y: cy - height / 2, width, height });
  }

  function reset(): void {
    doubleTap.cancel();
    apply({ ...base });
  }

  function onWheel(ev: WheelEvent): void {
    ev.preventDefault();
    doubleTap.cancel();
    const rect = svg.getBoundingClientRect();
    if (!(rect.width > 0 && rect.height > 0)) return;
    const delta =
      ev.deltaMode === 1 ? ev.deltaY * 16 : ev.deltaMode === 2 ? ev.deltaY * 100 : ev.deltaY;
    const factor = Math.exp(-delta * WHEEL_SENSITIVITY);
    const anchor = clientToViewBox(rect, vb, ev.clientX, ev.clientY);
    apply(zoomViewBoxAbout(vb, anchor, factor, base, limits));
  }

  function onPointerDown(ev: PointerEvent): void {
    if (ev.pointerType === 'mouse' && ev.button !== 0) return;
    const active = document.activeElement;
    if (active instanceof HTMLElement && !svg.contains(active)) active.blur();
    if (!(ev.target instanceof Element) || !isPanTarget(ev.target)) return;
    if (ev.pointerType === 'touch') {
      doubleTap.begin(ev.pointerId, { x: ev.clientX, y: ev.clientY });
    }
    pointers.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });
    try {
      svg.setPointerCapture(ev.pointerId);
    } catch {
      /* jsdom has no pointer capture */
    }
    svg.dataset.panning = 'true';
    ev.preventDefault();
  }

  function onPointerMove(ev: PointerEvent): void {
    const prev = pointers.get(ev.pointerId);
    if (!prev) return;
    if (ev.pointerType === 'touch') {
      doubleTap.move(ev.pointerId, { x: ev.clientX, y: ev.clientY });
    }
    const rect = svg.getBoundingClientRect();
    if (!(rect.width > 0 && rect.height > 0)) return;
    if (pointers.size === 1) {
      const scale = unitScale(rect, vb);
      const dx = (ev.clientX - prev.x) / scale;
      const dy = (ev.clientY - prev.y) / scale;
      pointers.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });
      apply({ ...vb, x: vb.x - dx, y: vb.y - dy });
      return;
    }
    const ids = [...pointers.keys()];
    const idA = ids[0];
    const idB = ids[1];
    if (idA === undefined || idB === undefined) return;
    const a0 = pointers.get(idA)!;
    const b0 = pointers.get(idB)!;
    const moved = { x: ev.clientX, y: ev.clientY };
    const a1 = idA === ev.pointerId ? moved : a0;
    const b1 = idB === ev.pointerId ? moved : b0;
    const d0 = Math.hypot(b0.x - a0.x, b0.y - a0.y) || 1;
    const d1 = Math.hypot(b1.x - a1.x, b1.y - a1.y) || 1;
    const mid0 = { x: (a0.x + b0.x) / 2, y: (a0.y + b0.y) / 2 };
    const mid1 = { x: (a1.x + b1.x) / 2, y: (a1.y + b1.y) / 2 };
    // Scale about the previous midpoint, then pan by the midpoint's motion.
    const anchor = clientToViewBox(rect, vb, mid0.x, mid0.y);
    let next = zoomViewBoxAbout(vb, anchor, d1 / d0, base, limits);
    const scaleNext = unitScale(rect, next);
    next = {
      ...next,
      x: next.x - (mid1.x - mid0.x) / scaleNext,
      y: next.y - (mid1.y - mid0.y) / scaleNext,
    };
    pointers.set(ev.pointerId, moved);
    apply(next);
  }

  function endPointer(ev: PointerEvent, canceled: boolean): void {
    const shouldReset =
      ev.pointerType === 'touch' &&
      doubleTap.end(ev.pointerId, { x: ev.clientX, y: ev.clientY }, canceled);
    if (!pointers.delete(ev.pointerId)) return;
    try {
      svg.releasePointerCapture(ev.pointerId);
    } catch {
      /* jsdom has no pointer capture */
    }
    if (pointers.size === 0) delete svg.dataset.panning;
    if (shouldReset) reset();
  }

  function onPointerUp(ev: PointerEvent): void {
    endPointer(ev, false);
  }

  function onPointerCancel(ev: PointerEvent): void {
    endPointer(ev, true);
  }

  function onDblClick(ev: MouseEvent): void {
    if (!(ev.target instanceof Element) || !isPanTarget(ev.target)) return;
    ev.preventDefault();
    reset();
  }

  svg.addEventListener('wheel', onWheel, { passive: false });
  svg.addEventListener('pointerdown', onPointerDown);
  svg.addEventListener('pointermove', onPointerMove);
  svg.addEventListener('pointerup', onPointerUp);
  svg.addEventListener('pointercancel', onPointerCancel);
  svg.addEventListener('dblclick', onDblClick);

  apply({ ...base });

  return {
    getView: currentView,
    setView,
    reset,
    dispose() {
      if (disposed) return;
      disposed = true;
      svg.removeEventListener('wheel', onWheel);
      svg.removeEventListener('pointerdown', onPointerDown);
      svg.removeEventListener('pointermove', onPointerMove);
      svg.removeEventListener('pointerup', onPointerUp);
      svg.removeEventListener('pointercancel', onPointerCancel);
      svg.removeEventListener('dblclick', onDblClick);
      svg.style.touchAction = prevTouchAction;
      delete svg.dataset.panning;
      pointers.clear();
      doubleTap.cancel();
    },
  };
}
